import { Filter } from "~/modules/filters/types";
import { MEASURE_SLUGS } from "~/consts";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";
import { getApyChart } from "~/modules/asset_timeseries_v4/shared/getApyChart";
import { getAllTokensStackedChart } from "~/modules/token_timeseries/shared/getAllTokensStackedChart";
import { getHoldersChart } from "~/modules/token_timeseries/shared/getHoldersChart";
import { getMintBurnVolumeChart } from "~/modules/token_timeseries/shared/getMintBurnVolumeChart";
import {
    LegendProps,
    NavigatorControlState,
    VisualizationMode,
} from "~/modules/visualization/components/Timeseries/types";
import { getActiveAddressesChart } from "~/modules/token_timeseries/shared/getActiveAddressesChart";
import { getTransferVolumeChart } from "~/modules/token_timeseries/shared/getTransferVolumeChart";

// // // //

export enum MetricTypes {
    TotalValue = "total_value",
    AssetHolders = "asset_holders",
    ActiveAddresses = "active_addresses",
    TransferVolume = "transfer_volume",
    MintBurnVolume = "mint_burn_volume",
    Apy7Day = "apy_7_day",
}

type LoginRequirementConfig = Record<MetricTypes, boolean>;

const DEFAULT_LOGIN_REQUIREMENT_CONFIG: LoginRequirementConfig = {
    [MetricTypes.TotalValue]: false,
    [MetricTypes.AssetHolders]: true,
    [MetricTypes.ActiveAddresses]: true,
    [MetricTypes.TransferVolume]: true,
    [MetricTypes.MintBurnVolume]: true,
    [MetricTypes.Apy7Day]: true,
};

export function getTimeseriesModes(props: {
    analyticsKey: string;
    filters: Filter[];
    assetTimeseriesFilters?: Filter[];
    metrics?: MetricTypes[];
    height?: number;
    legend?: LegendProps;
    navigator?: NavigatorControlState;
    defaultGroupBy: TokenTimeseriesGroupBy;
    groupByOptions?: TokenTimeseriesGroupBy[];
    groupByOptionsWithNewIndicator?: TokenTimeseriesGroupBy[];
    groupByOptionsLabelOverrides?: Partial<
        Record<TokenTimeseriesGroupBy, string>
    >;
    measureSlug?: string;
    loginRequirementConfig?: LoginRequirementConfig;
}): VisualizationMode[] {
    const {
        analyticsKey,
        filters,
        assetTimeseriesFilters,
        defaultGroupBy,
        height = 350,
        measureSlug = MEASURE_SLUGS.bridged_token_value_dollar,
        legend,
        navigator,
        loginRequirementConfig = DEFAULT_LOGIN_REQUIREMENT_CONFIG,
        metrics = [
            MetricTypes.TotalValue,
            MetricTypes.AssetHolders,
            MetricTypes.ActiveAddresses,
            MetricTypes.TransferVolume,
            MetricTypes.MintBurnVolume,
        ],
        groupByOptions = [
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.assetClass,
            TokenTimeseriesGroupBy.network,
            TokenTimeseriesGroupBy.protocol,
            TokenTimeseriesGroupBy.manager,
            TokenTimeseriesGroupBy.jurisdictionCountry,
        ],
        groupByOptionsWithNewIndicator,
        groupByOptionsLabelOverrides,
    } = props;

    const modes = [];

    if (metrics.includes(MetricTypes.TotalValue)) {
        const TOTAL_ASSET_VALUE = getAllTokensStackedChart({
            key: `${analyticsKey}-market-caps`,
            label: "Total Value",
            filters,
            defaultGroupBy,
            enableGroupByControls: true,
            measureSlug,
            ignoreDownloadsFeatureFlag: true,
            groupByOptions,
            groupLimit: 50,
            legend,
            navigator,
            height,
            loginRequired: loginRequirementConfig[MetricTypes.TotalValue],
        });

        modes.push(TOTAL_ASSET_VALUE);
    }

    if (metrics.includes(MetricTypes.AssetHolders)) {
        const HOLDERS = getHoldersChart({
            key: `${analyticsKey}-holders`,
            filters,
            defaultGroupBy,
            enableGroupByControls: true,
            groupByOptions,
            groupLimit: 50,
            legend,
            navigator,
            height,
            loginRequired: loginRequirementConfig[MetricTypes.AssetHolders],
        });

        modes.push(HOLDERS);
    }

    if (metrics.includes(MetricTypes.ActiveAddresses)) {
        const ACTIVE_ADDRESSES = getActiveAddressesChart({
            key: `${analyticsKey}-active-addresses`,
            filters,
            defaultGroupBy,
            enableGroupByControls: true,
            groupByOptions,
            groupLimit: 50,
            legend,
            navigator,
            height,
            loginRequired: loginRequirementConfig[MetricTypes.ActiveAddresses],
        });

        modes.push(ACTIVE_ADDRESSES);
    }

    if (metrics.includes(MetricTypes.TransferVolume)) {
        const TRANSFER_VOLUME = getTransferVolumeChart({
            key: `${analyticsKey}-transfer-volume`,
            filters,
            defaultGroupBy,
            enableGroupByControls: true,
            groupByOptions,
            groupLimit: 50,
            legend,
            navigator,
            height,
            loginRequired: loginRequirementConfig[MetricTypes.TransferVolume],
        });

        modes.push(TRANSFER_VOLUME);
    }

    if (metrics.includes(MetricTypes.MintBurnVolume)) {
        const MINT_BURN_VOLUME = getMintBurnVolumeChart({
            key: `${analyticsKey}-fund-flows`,
            filters,
            legend,
            navigator,
            height,
            loginRequired: loginRequirementConfig[MetricTypes.MintBurnVolume],
        });

        modes.push(MINT_BURN_VOLUME);
    }

    if (metrics.includes(MetricTypes.Apy7Day)) {
        const APY_7_DAY = getApyChart({
            key: `${analyticsKey}-apy-7-day`,
            label: "7D APY",
            filters: assetTimeseriesFilters ?? [],
            enableGroupByControls: false,
            measureSlug: MEASURE_SLUGS.apy_7_day,
            groupLimit: 50,
            legend,
            navigator,
            height,
            loginRequired: loginRequirementConfig[MetricTypes.Apy7Day],
        });

        modes.push(APY_7_DAY);
    }

    // Layer the page-level group-by control config (label overrides +
    // new-option callouts) onto every mode with group-by controls
    return modes.map((mode) => {
        const groupBy = mode.queryControls?.groupBy;
        if (!groupBy) {
            return mode;
        }

        return {
            ...mode,
            queryControls: {
                ...mode.queryControls,
                groupBy: {
                    ...groupBy,
                    labelOverrides: groupByOptionsLabelOverrides,
                    optionsWithNewIndicator: groupByOptionsWithNewIndicator,
                } as typeof groupBy,
            },
        };
    });
}
